import {sleep} from "@variocube/app-common";
import {
    Badge,
    classNames,
    CodeDigits,
    IconNames,
    KeyPad,
    layout,
    MaterialIcon,
    PushButton,
    TextInput,
    theme,
    typography,
    useAsyncEffect,
    useTypeRef,
    View
} from "@variocube/cube-ui";
import * as React from "react";
import {useRef, useState} from "react";
import {keyframes, stylesheet} from "typestyle";
import {useLocalization} from "../../i18n";

interface VerificationCodeProps {
    onVerified: () => void;
    onBack: () => void;
    code: string;
}

export function BookingCodeVerification({onVerified, onBack, code}: VerificationCodeProps) {
    const [enteredCode, setEnteredCode] = useState("");
    const {t} = useLocalization();
    const input = useRef<HTMLInputElement | null>();
    const [type, setType] = useTypeRef();

    const verified = code == enteredCode;

    useAsyncEffect(async () => {
        if (verified) {
            await sleep(2000);
            onVerified();
        }
    }, [verified, onVerified]);

    return (
        <View className={classNames(layout.center, layout.alignCenter)}>
            <div className={classNames(layout.horizontal, styles.content)}>
                <div className={classNames(theme.lessContrast, layout.vertical, layout.spaceBetween, styles.box)}>
                    <div>
                        <div className={styles.title}>
                            {t("EnterVerificationCode")}
                        </div>
                        <p>
                            {t("EnterVerificationCodeInfo")}
                        </p>
                    </div>
                    {!verified && (enteredCode.length > 0 && enteredCode.length < code.length) && (
                        <p className={typography.muted}>
                            {t("MoreDigitsNeeded")}
                        </p>
                    )}
                    {!verified && enteredCode.length == code.length && (
                        <Badge error>
                            {t("VerificationCodeWrong")}
                        </Badge>
                    )}
                    <div>
                        <PushButton onClick={onBack}>
                            {t("Back")}
                        </PushButton>
                    </div>
                </div>

                <div className={classNames(theme.moreContrast)}>
                    <TextInput
                        type={verified ? "password" : "text"}
                        onChange={setEnteredCode}
                        value={enteredCode}
                        disabled={verified}
                        inputRef={input}
                        typeRef={setType}
                        placeholder={t("BookingCodePlaceholder")}
                        className={classNames(styles.textInput)}
                    />
                    <div className={styles.keyPadContainer}>
                        <KeyPad
                            onType={type}
                            digits={CodeDigits}
                            disabled={verified}
                        />
                    </div>
                </div>

                {verified && (
                    <div className={styles.verifiedOverlay}>
                        <div className={styles.verifiedOverlayContent}>
                            <div className={styles.checkIcon}>
                                <MaterialIcon icon={IconNames.check}/>
                            </div>
                            <p>
                                {t("BookingCodeCorrect")}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </View>
    );
}


const styles = stylesheet({
    content: {
        position: "relative"
    },
    title: {
        fontSize: "1.5em",
    },
    box: {
        width: "20em",
        padding: "1.5em"
    },
    keyPadContainer: {
        position: "relative",
    },
    verifiedOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "var(--color-success)",
        animationName: keyframes({
            from: { opacity: 0 },
            to: { opacity: 1 }
        }),
        animationDelay: "0s",
        animationDuration: "0.5s",
        animationFillMode: "both",
    },
    verifiedOverlayContent: {
        display: "block",
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translateX(-50%) translateY(-50%)",
    },
    checkIcon: {
        fontSize: "4rem",
        lineHeight: 1,
    },
    textInput: {
        fontWeight: "bold",
        width: "24rem",
        height: "5rem",
    },
});